import { useId, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/shared/api/http';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useActiveModel, usePublishModel } from '../api/scoring';
import type { ScoreModel } from '../api/scoring';

/* The score model as the redesign draws it — the same editor in the Scores side panel and on the
 * admin route, so there is one answer to "how is a model published".
 *
 * **Publishing is confirmed, because it changes what every future score means.** Existing scores
 * keep the version they were computed under; the confirmation says so before the reader commits.
 *
 * **A field that is not a number, or is negative, holds the button.** The server refuses it with a
 * 422 either way; refusing it here names the field somebody was editing instead of a type.
 */

const SEVERITIES = ['info', 'warning', 'error', 'blocker'] as const;

interface Draft {
  readonly dimensions: Readonly<Record<string, string>>;
  readonly severities: Readonly<Record<string, string>>;
  readonly notes: string;
}

function draftOf(model: ScoreModel): Draft {
  return {
    dimensions: Object.fromEntries(
      Object.entries(model.dimension_weights).map(([key, value]) => [key, String(value)]),
    ),
    severities: Object.fromEntries(
      SEVERITIES.map((key) => [key, String(model.severity_weights[key] ?? 0)]),
    ),
    notes: '',
  };
}

function invalid(raw: string): boolean {
  if (raw.trim() === '') return true;
  const value = Number(raw);
  return !Number.isFinite(value) || value < 0;
}

function numbers(values: Readonly<Record<string, string>>): Record<string, number> {
  return Object.fromEntries(
    Object.entries(values).map(([key, raw]) => [key, Number(raw)]),
  );
}

export function ScoreModelEditor({
  canPublish,
}: {
  /** Whether this reader may publish. Without it the weights are shown and cannot be edited. */
  readonly canPublish: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const fieldId = useId();
  const model = useActiveModel();
  const publish = usePublishModel();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);

  if (model.isPending) return <SkeletonRows rows={4} columns={2} />;

  /* Only a 404 means "nothing published yet". Any other failure is a failure, and calling it an
     empty model would offer to publish defaults over a model that may well exist. */
  if (model.isError) {
    if (!(model.error instanceof ApiError) || model.error.status !== 404) {
      return (
        <Alert tone="error" title={t('scoring.model.unreadable')}>
          {model.error.message}
        </Alert>
      );
    }
    return (
      <div className="lq-sm__none">
        <div className="lq-sm__noneTitle">{t('scoring.model.none')}</div>
        {canPublish ? (
          <button
            type="button"
            className="lq-btn lq-btn--primary"
            disabled={publish.isPending}
            onClick={() => {
              publish.mutate({ weights: {}, notes: null });
            }}
          >
            {t('scoring.model.publishDefaults')}
          </button>
        ) : (
          <p className="lq-sm__hint">{t('scoring.model.needsAdmin')}</p>
        )}
        {publish.isError ? <Alert tone="error">{publish.error.message}</Alert> : null}
      </div>
    );
  }

  const active = model.data;
  const base = draftOf(active);
  const current = draft ?? base;
  const dimensionKeys = Object.keys(active.dimension_weights).sort();
  const changed =
    dimensionKeys.some(
      (key) => Number(current.dimensions[key]) !== active.dimension_weights[key],
    ) ||
    SEVERITIES.some(
      (key) => Number(current.severities[key]) !== (active.severity_weights[key] ?? 0),
    );
  const anyInvalid =
    Object.values(current.dimensions).some(invalid) ||
    Object.values(current.severities).some(invalid);
  const blocked = !canPublish || !changed || anyInvalid;
  const next = active.version + 1;

  const hint = anyInvalid
    ? t('scoring.model.hintInvalid')
    : !changed
      ? t('scoring.model.hintUnchanged')
      : t('scoring.model.hintPublishes', { version: next });

  const update = (patch: Partial<Draft>): void => {
    setDraft({ ...current, ...patch });
    setActiveVersion(null);
  };

  return (
    <div className="lq-sm">
      <div className="lq-sm__version">
        {t('scoring.model.versionLine', {
          version: active.version,
          who: personName(active.published_by),
          ago: ageText(t, active.published_at, new Date()) ?? active.published_at,
        })}
      </div>

      <div>
        <div className="lq-sm__heading">{t('scoring.model.dimensionWeights')}</div>
        <div className="lq-sm__grid">
          {dimensionKeys.map((key) => {
            const raw = current.dimensions[key] ?? '';
            return (
              /* A container rather than a wrapping `<label>`: the dimension's "i" must sit beside
                 its name, and a button inside a `<label>` would join the input's accessible name
                 and focus the input when pressed. `htmlFor` keeps the click-to-focus. */
              <div key={key} className="lq-sm__field">
                <HelpLabel
                  subject={t(`scoring.dimension.${key}`)}
                  help={t(`help.dimension.${key}`)}
                >
                  <label htmlFor={`${fieldId}-${key}`}>
                    {t(`scoring.dimension.${key}`)}
                  </label>
                </HelpLabel>
                <input
                  id={`${fieldId}-${key}`}
                  className="lq-sm__input"
                  inputMode="decimal"
                  value={raw}
                  disabled={!canPublish}
                  aria-invalid={invalid(raw) ? true : undefined}
                  onChange={(event) => {
                    update({
                      dimensions: { ...current.dimensions, [key]: event.target.value },
                    });
                  }}
                />
                {key === 'accuracy' ? (
                  <span className="lq-sm__note">{t('scoring.model.accuracyShort')}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="lq-sm__heading">{t('scoring.model.severityWeights')}</div>
        <div className="lq-sm__lede">{t('scoring.model.severityLede')}</div>
        <div className="lq-sm__grid lq-sm__grid--four">
          {SEVERITIES.map((key) => {
            const raw = current.severities[key] ?? '';
            return (
              <label key={key} className="lq-sm__field">
                {t(`rules.severity.${key}.label`)}
                <input
                  className="lq-sm__input"
                  inputMode="decimal"
                  value={raw}
                  disabled={!canPublish}
                  aria-invalid={invalid(raw) ? true : undefined}
                  onChange={(event) => {
                    update({
                      severities: { ...current.severities, [key]: event.target.value },
                    });
                  }}
                />
              </label>
            );
          })}
        </div>
      </div>

      <label className="lq-sm__field lq-sm__field--notes">
        {t('scoring.model.notes')}
        <input
          className="lq-sm__input"
          value={current.notes}
          maxLength={500}
          disabled={!canPublish}
          placeholder={t('scoring.model.notesPlaceholder')}
          onChange={(event) => {
            update({ notes: event.target.value });
          }}
        />
      </label>

      {canPublish ? (
        <div className="lq-sm__actions">
          <button
            type="button"
            className="lq-btn lq-btn--secondary"
            onClick={() => {
              setDraft(null);
            }}
          >
            {t('scoring.model.reset')}
          </button>
          <button
            type="button"
            className="lq-btn lq-btn--primary"
            disabled={blocked || publish.isPending}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('scoring.model.publish')}
          </button>
          <span className="lq-sm__hint">{hint}</span>
        </div>
      ) : (
        <p className="lq-sm__hint">{t('scoring.model.needsAdmin')}</p>
      )}

      {activeVersion === null ? null : (
        <p className="lq-sm__done" role="status">
          {t('scoring.model.nowActive', { version: activeVersion })}
        </p>
      )}
      {publish.isError ? (
        <Alert tone="error" title={t('scoring.model.refused')}>
          {publish.error.message}
        </Alert>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title={t('scoring.model.confirmTitle', { version: next })}
        message={t('scoring.model.confirmBody')}
        confirmLabel={t('scoring.model.confirmAction', { version: next })}
        cancelLabel={t('common.cancel')}
        tone="primary"
        busy={publish.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          publish.mutate(
            {
              weights: numbers(current.dimensions),
              severityWeights: numbers(current.severities),
              notes: current.notes.trim() === '' ? null : current.notes.trim(),
            },
            {
              onSuccess: (published) => {
                setConfirming(false);
                setDraft(null);
                setActiveVersion(published.version);
              },
              onError: () => {
                setConfirming(false);
              },
            },
          );
        }}
      />
    </div>
  );
}
